import {Box3, MathUtils, Sphere, Vector3} from "three";
import * as Globals from "../globals";
import {buildingGroup, layoutView} from "../main";
import {layoutCamera, layoutControls, render, sceneCamera, sceneControls} from "../sceneManager";
import {toFixedFloat} from "./utils";
import {setBox3FromObjectArray} from "./threeUtils";

function computeCameraDistanceForObject(boundingSphere, camera) {
    let directionVector = new Vector3().subVectors(boundingSphere.center, camera.position);
    console.log(directionVector);

    // Get the aspect ratio of the camera (width/height)
    const aspect = camera.aspect;

    // Convert camera's vertical field of view (fov) from degrees to radians
    const fovRad = MathUtils.degToRad(camera.fov);

    // Get the radius of the bounding sphere.
    const radius = boundingSphere.radius;

    // Distance calculation based on trigonometry
    // Assuming the radius should fit both width and height of camera's view.
    const distanceHorizontal = radius / (Math.tan(fovRad / 2) * aspect);
    const distanceVertical = radius / Math.tan(fovRad / 2);
    const distance = Math.max(distanceHorizontal, distanceVertical);

    // Create a position for the camera based on the direction vector and calculated distance
    const directionNormalized = directionVector.clone().normalize();
    const cameraPosition = boundingSphere.center.clone().sub(directionNormalized.multiplyScalar(distance));

    // Update the camera's position
    // camera.position.copy(cameraPosition);

    // Look at the object's center
    camera.lookAt(boundingSphere.center.x, 0, boundingSphere.center.z);
    console.log(camera);

    return distance;
}

export function fitPerspectiveCameraToBounds(camera, controls, bounds, fitOffset = 1) {
    let sphere = bounds.getBoundingSphere(new Sphere());

    const size = bounds.getSize(new Vector3());
    const center = bounds.getCenter(new Vector3());

    const maxSize = sphere.radius * 2;
    const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
    const fitWidthDistance = fitHeightDistance / camera.aspect;
    const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);

    const direction = controls.target.clone().sub(camera.position);
    if (direction.lengthSq() < 1e-6) {
        direction.set(1, 0.8, 1);
    }
    direction.normalize().multiplyScalar(distance);

    controls.target.copy(center);
    camera.updateProjectionMatrix();
    camera.position.copy(controls.target).sub(direction);
    controls.update();
}

export function frameBuilding(fitOffset = 1.45) {
    if (!sceneCamera || !sceneControls || !buildingGroup?.children?.length) {
        return;
    }

    fitPerspectiveCameraToObject(sceneCamera, sceneControls, buildingGroup, fitOffset);
    render();
}

export function fitPerspectiveCameraToObject(camera, controls, selection, fitOffset = 1) {
    let bounds = setBox3FromObjectArray(selection.children);

    if (bounds.isEmpty()) {
        return;
    }

    fitPerspectiveCameraToBounds(camera, controls, bounds, fitOffset);
}

export function zoomOrthographicCameraToBounds(camera, bounds, desiredAspectRatio, scaleFactor = Globals.defaultZoomFactor, controls = null) {
    // Calculate object dimensions and center
    let objectCenter = bounds.getCenter(new Vector3());

    //Pan to center point
    let deltaX, deltaY;
    const point1Screen = objectCenter.clone().project(camera);

    deltaX = -point1Screen.x * layoutView.clientWidth / 2;
    deltaY = point1Screen.y * layoutView.clientHeight / 2;

    if (controls !== null) {
        controls.panTo(deltaX, deltaY);
    } else if (camera === layoutCamera) {
        layoutControls.panTo(deltaX, deltaY);
    } else {
        sceneControls.panTo(deltaX, deltaY);
    }

    let objectWidth = bounds.max.x - bounds.min.x;
    let objectHeight = bounds.max.y - bounds.min.y;
    let objectDepth = bounds.max.z - bounds.min.z;

    camera.lookAt(objectCenter); //Camera is positioned to look at camera center. Make camera direction look at center too.
    //Camera direction to check which plane we're looking at
    let cameraDirection = camera.getWorldDirection(new Vector3());

    if (toFixedFloat(Math.abs(cameraDirection.x), 0) === 1) {
        //Camera in X-Direction > Objects depth and height are relevant
        camera.left = (-objectDepth / 2);
        camera.right = (objectDepth / 2);
        camera.top = (objectHeight / 2);
        camera.bottom = (-objectHeight / 2);
    } else if (toFixedFloat(Math.abs(cameraDirection.y), 0) === 1) {
        //Camera in X-Direction > Objects depth and width are relevant
        camera.left = (-objectWidth / 2);
        camera.right = (objectWidth / 2);
        camera.top = (objectDepth / 2);
        camera.bottom = (-objectDepth / 2);
    } else if (toFixedFloat(Math.abs(cameraDirection.z), 0) === 1) {
        //Camera in X-Direction > Objects width and height are relevant
        camera.left = (-objectWidth / 2);
        camera.right = (objectWidth / 2);
        camera.top = (objectHeight / 2);
        camera.bottom = (-objectHeight / 2);
    }

    // Calculate the current aspect ratio
    let frustumWidth = camera.right - camera.left;
    let frustumHeight = camera.top - camera.bottom;
    let currentAspect = frustumWidth / frustumHeight;

    //Modify the width or height of the camera depending on the currentRatio
    let factor = currentAspect / desiredAspectRatio > 1 ? currentAspect / desiredAspectRatio : desiredAspectRatio / currentAspect;
    if (currentAspect < desiredAspectRatio) {
        camera.left *= factor;
        camera.right *= factor;
    } else if (currentAspect > desiredAspectRatio) {
        camera.top *= factor;
        camera.bottom *= factor;
    }

    //Apply zoom to controls and camera
    camera.zoom = 1 / scaleFactor;

    camera.updateProjectionMatrix();
}

export function zoomOrthographicCameraToObject(camera, object, scaleFactor = Globals.defaultZoomFactor) {
    //Get buildingGroup bounds
    const bounds = new Box3();
    bounds.setFromObject(object);

    if (bounds.isEmpty()) {
        return; //Zoomen auf leere Extents vermeiden
    }

    zoomOrthographicCameraToBounds(camera, bounds, camera.aspect, scaleFactor);
}
